import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  Search as SearchIcon,
  Users,
  CalendarDays,
  Building2,
} from "lucide-react";

import { useModuleEnabled } from "../access/useFeatureAccess";
import { useAuthContext } from "../auth/AuthContext";
import { isEmbeddedInExtension } from "../bridge/types";
import { useForegroundChartPatientId } from "../bridge/useElationBridge";
import HomePage from "../home/HomePage";
import MemberProfilePage, {
  type MemberProfilePageProps,
} from "../members/MemberProfilePage";
import MemberSearchPage from "../members/MemberSearchPage";
import PracticeSchedulePage from "../schedule/PracticeSchedulePage";
import WorklistPage from "../worklist/WorklistPage";
import HospitalEdPage from "../hospitalEd/HospitalEdPage";
import { AppHeader, EmbeddedControls, useTopNavWorklistVisible } from "./AppLayout";
import { MemberSearchGate, PracticeScheduleGate } from "./routeGates";
import {
  activeTabForPath,
  profileSegment,
  shouldLeaveHomeForChart,
} from "./tabShellRouting";
import {
  PROFILE_TAB_TITLE_PLACEHOLDER,
  getInitials,
  openProfileSlot,
  upgradeProfileTitle,
  type OpenProfile,
} from "./profileTabs";
import { TabShellNav, type FixedTabSpec } from "./TabShellNav";

// Computed once at module load — mirrors AppLayout.tsx's own EMBEDDED
// constant (the embedded-vs-standalone state can't change during a page's
// lifetime; each module independently checking the window hierarchy is
// cheap and avoids a cross-module singleton).
const EMBEDDED = isEmbeddedInExtension();

/**
 * P6b/P6d — stateful, keep-alive tab shell for standalone web AND (as of P6d)
 * the extension side panel.
 *
 * Member Search (`/members`), the open Member Profile (`/members/:id`, which
 * REUSES the Search tab rather than owning a tab of its own — see below), and
 * — when their flag/persona allow — Practice Schedule (`/schedule`) and
 * Priority Worklist (`/worklist`) are views the user switches between FREELY
 * WITHOUT LOSING PROGRESS. Rather than a route swap
 * that remounts each view from scratch, every open view stays MOUNTED and is
 * shown/hidden via the `hidden` attribute (`display:none`), so React state
 * (query text, results, filters, sort, per-patient sub-tab/scroll offsets)
 * survives every switch.
 *
 * TRADEOFF: Search + Schedule + Worklist + ONE member profile (which owns a
 * GraphQLProvider/Apollo subtree) can stay resident in memory at once — an
 * accepted, bounded cost for the stateful UX.
 *
 * SINGLE-MEMBER SEMANTICS — there is NO member tab. AstraConnect enforces
 * PROVIDER FOCUS: exactly one member may be open, and it REUSES the permanent
 * Search tab's slot instead of adding a tab of its own. The tab strip is
 * therefore fixed (Search, Practice Schedule, Priority Worklist) and never
 * grows. While a profile is on screen, the Search tab relabels to that member
 * (name + initials chip) and grows a close button; back on `/members` it reads
 * "Search" again — the tab's label always describes what is actually
 * displayed. Three ways out of a profile, all landing on the search results
 * with their query/filters intact: the profile's own "Back to Home" header
 * button (dirty-form aware — see MemberProfilePage's `confirmLeaveIfDirty`),
 * clicking the tab itself, or the tab's close button.
 *
 * Opening a member (from Search, a Worklist row, or a deep link) fills the slot
 * keyed by navId: re-opening the ALREADY-OPEN member reuses the same mounted
 * instance and its state (never re-fetched or reset), while opening a DIFFERENT
 * member REPLACES it — the outgoing profile unmounts and releases its Apollo
 * subtree. `key={navId}` is what gives each newly-opened member a clean
 * instance. Its label is the member's name when known up front (Search/Worklist
 * selection carries it via `location.state.tabTitle`) or a placeholder
 * ("Patient") that MemberProfilePage upgrades once resolved
 * (`onMemberResolved` — see its doc there); a Worklist row's selected-member
 * payload has no name field at all, so those always start on the placeholder.
 *
 * Navigating from a profile to Search KEEPS the profile mounted (hidden), so
 * bouncing back to it is instant and preserves in-flight work such as a dirty
 * Create-Auth form. The close button is the explicit way to DISCARD it.
 *
 * URL SYNC: the browser URL always tracks the ACTIVE tab (`/members`,
 * `/members/:id`, `/worklist`), so refresh and back/forward keep working —
 * revisiting a `/members/:id` whose tab was closed re-opens it (placeholder
 * title, same as any other deep link).
 */
/**
 * Nav glyph size, one step ABOVE the header utilities' 16px (the theme
 * toggle's SVG and NotificationBell's `h-4 w-4` bell).
 *
 * These were 15px — a pixel UNDER the utilities they are supposed to outrank,
 * while also sharing their `text-muted-foreground` tone. That combination is
 * why the nav read as more toolbar rather than as primary wayfinding. Size and
 * tone are now both stepped (see `tabToneClass` in TabShellNav.tsx); shape
 * carries the rest (rectangular tinted pills here, circular ghosts there).
 *
 * Kept as one constant because all four fixed tabs must move together — a
 * mixed set is worse than a uniformly small one.
 */
const NAV_ICON_SIZE = 18;

export default function TabbedAppShell() {
  const { accountLabel, logout } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const worklistEnabled = useTopNavWorklistVisible();
  // Practice Schedule (ACT-1239) is persona-gated ONLY (no VITE flag) — the tab
  // and its keep-alive panel appear iff the active persona grants the module
  // (allow-all when no persona is active, same seam as MemberSearch). Mirrors
  // the /schedule route gate (PracticeScheduleGate) so tab + route stay in sync.
  const scheduleEnabled = useModuleEnabled("PracticeSchedule");

  // The ONE open member profile, or null when none is open. Not a list: the
  // member reuses the Search tab and there is no member tab strip to order.
  const [openProfile, setOpenProfile] = useState<OpenProfile | null>(null);
  // Tracks which navigation (`location.key`) has already been folded into
  // `openProfile` — lets a profile-route location be synced into state EXACTLY
  // ONCE per navigation (fresh open, browser back/forward, or a deep link/hard
  // refresh) using React's supported "adjust state during render" pattern
  // (compute the derived value now, for THIS render, and commit it via
  // setState) — no effect-driven extra render/flash, and no infinite loop
  // since the guard condition becomes false the instant the state commits.
  const [lastSyncedKey, setLastSyncedKey] = useState<string | null>(null);

  const active = activeTabForPath(location.pathname, worklistEnabled);
  const seg = profileSegment(location.pathname);
  const currentNavId = seg !== undefined ? decodeURIComponent(seg) : null;

  let effectiveProfile = openProfile;
  // `openProfile === null` is part of the guard, not just the key check: after
  // the close button clears the slot, a browser Back to that same `/members/:id`
  // history entry replays its ORIGINAL `location.key`. Keying alone would treat
  // it as already-synced and render an empty profile pane, so a null slot on a
  // profile route always re-opens.
  if (
    active === "profile" &&
    currentNavId &&
    (location.key !== lastSyncedKey || openProfile === null)
  ) {
    const carriedTitle = (location.state as { tabTitle?: string } | null)?.tabTitle;
    const retainedTitle =
      openProfile?.navId === currentNavId ? openProfile.title : undefined;
    const nextProfile = openProfileSlot(openProfile, {
      navId: currentNavId,
      title: carriedTitle || retainedTitle || PROFILE_TAB_TITLE_PLACEHOLDER,
      location: { pathname: location.pathname, state: location.state ?? null },
    });
    if (openProfile && openProfile.navId !== currentNavId) {
      // The NORMAL path under the one-member constraint, not an error: the
      // outgoing profile unmounts on this render.
      // eslint-disable-next-line no-console
      console.debug(
        `[TabbedAppShell] replaced open member ${openProfile.navId} with ${currentNavId}`,
      );
    }
    effectiveProfile = nextProfile;
    setOpenProfile(nextProfile);
    setLastSyncedKey(location.key);
  }

  const handleMemberResolved = useCallback((navId: string, name: string) => {
    setOpenProfile((prev) => upgradeProfileTitle(prev, navId, name));
  }, []);

  // Close button on the (member-hosting) tab: DISCARD the open member —
  // unmounting it and releasing its Apollo subtree — and return to HOME,
  // the same place the profile's own "Back to Home" header goes. Distinct
  // from merely clicking the tab, which shows the search results but keeps
  // the profile mounted for an instant return.
  const handleCloseMember = useCallback(() => {
    setOpenProfile(null);
    navigate("/home");
  }, [navigate]);

  // Lazy-mount the Worklist tab: a worklist-enabled provider who never opens
  // the tab must not have its patient-panel query fire at load. Flips true
  // the first time the tab is actually selected, then STAYS true — same
  // "mount once, toggle via `hidden` afterward" keep-alive contract as every
  // patient profile tab above. Must run BEFORE the `active === null` early
  // return below so this hook always fires in the same order across renders.
  const [worklistActivated, setWorklistActivated] = useState(active === "worklist");
  useEffect(() => {
    if (active === "worklist" && !worklistActivated) setWorklistActivated(true);
  }, [active, worklistActivated]);

  // Lazy-mount the Practice Schedule tab the same way — a schedule-enabled
  // persona who never opens the tab must not have its practiceSchedule query
  // fire at load. Flips true the first time it's selected, then STAYS true
  // (mount once, toggle via `hidden` afterward — same keep-alive contract).
  const [scheduleActivated, setScheduleActivated] = useState(active === "schedule");
  useEffect(() => {
    if (active === "schedule" && !scheduleActivated) setScheduleActivated(true);
  }, [active, scheduleActivated]);

  // Lazy-mount the Hospital/ED tab the same way. Shares `worklistEnabled`
  // (same flag+persona as Worklist — see HospitalEdGate), so a provider who
  // never opens EITHER tab never fires either query at load. Unlike the two
  // latches above, this uses the "adjust state during render" pattern
  // (React's supported alternative to a derived-state effect — the SAME
  // idiom this file's `openProfile`/`lastSyncedKey` sync already uses
  // earlier) instead of a `useEffect`: no extra render/flash, and the guard
  // condition goes false the instant the state commits, so it can't loop.
  const [hospitalEdActivated, setHospitalEdActivated] = useState(active === "hospitalEd");
  if (active === "hospitalEd" && !hospitalEdActivated) setHospitalEdActivated(true);

  // An in-context chart beats the Home landing page.
  //
  // Home is the app's default route, so the extension side panel — which
  // iframes the app at "/" — now BOOTS onto Home rather than Search. Member
  // Search stays mounted behind it, but its two auto-navigate effects only
  // fire for the ACTIVE tab (see `isActiveTab` in MemberSearchPage), so the
  // in-context patient's auto-search ran invisibly and its navigation was
  // swallowed: auto-nav looked dead with no error anywhere.
  //
  // Rather than let a hidden tab navigate (the race `isActiveTab` exists to
  // stop), select Search when a chart is genuinely in the FOREGROUND, so
  // auto-nav runs on a tab the clinician can see. No chart in front → Home
  // stays Home. `shouldLeaveHomeForChart` carries the once-per-chart rule and
  // the reasoning behind it.
  //
  // The shell keeps its OWN, deliberately narrow bridge subscription rather
  // than reading Search's context: Search holds a STICKY patient (retained
  // across tab switches by design) and re-renders ~3x/second while a note is
  // open. This decision needs the LIVE foreground chart and nothing else, so
  // it subscribes to a single string — see useForegroundChartPatientId, which
  // explains why the shell must not re-render on the note cadence.
  //
  // EHR-agnostic: the bridge carries whichever adapter scraped the chart
  // (Elation, eCW, athenahealth, Office Ally), so this follows the foreground
  // chart in any of them, exactly as Search's own auto-nav does.
  const chartPatientId = useForegroundChartPatientId();
  const redirectedForChartRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      !shouldLeaveHomeForChart({
        activeTab: active,
        chartPatientId,
        lastRedirectedPatientId: redirectedForChartRef.current,
      })
    ) {
      return;
    }
    redirectedForChartRef.current = chartPatientId;
    navigate("/members", { replace: true });
  }, [active, chartPatientId, navigate]);

  if (active === null) return <Navigate to="/home" replace />;

  // The member currently DISPLAYED in the Search tab's slot, if any. Gated on
  // `active === "profile"` (not merely "a profile is open") so the tab's label
  // always describes what's on screen: back on /members it reads "Search"
  // again, even though the profile stays mounted behind it.
  const hostedMember = active === "profile" ? effectiveProfile : null;

  const fixedTabs: FixedTabSpec[] = [];
  /*
   * No Home tab and no Search tab. The LOGO is the Home affordance now (see
   * AppLayout's `AppHeader`, which links the mark + wordmark to /home), and
   * member search moved into a Home widget (home/MemberSearchWidget.tsx), so
   * both buttons were pointing at things the user already has in front of
   * them.
   *
   * Only the BUTTONS are gone — both routes stay exactly as they were. This
   * array feeds `TabShellNav` and nothing else; the keep-alive panes below
   * are keyed off `active`, not off these specs, so /home and /members are
   * still mounted and still reachable: `activeTabForPath` maps them, the
   * Elation bridge auto-navigates to /members, the `active === null` fallback
   * redirects there, and the widget's "Open in full search" link goes there.
   *
   * The member tab is the one thing this spec still has to produce. An open
   * profile is hosted in the SAME slot as search — that is where its name,
   * initials chip and close button come from (`handleCloseMember` is the only
   * close affordance a patient tab has). So the spec is now pushed only while
   * a member is actually hosted, rather than existing permanently as
   * "Search". `hostedMember` is already gated on `active === "profile"`, so
   * standing on /members itself shows no tab — that page carries its own
   * heading.
   */
  if (hostedMember) {
    fixedTabs.push({
      id: "search",
      label: hostedMember.title,
      to: "/members",
      // One tab, two views — highlighted for the member profile AND for the
      // search results it sits in front of.
      active: active === "search" || active === "profile",
      icon: <SearchIcon size={NAV_ICON_SIZE} aria-hidden="true" />,
      initials: getInitials(hostedMember.title),
      onClose: handleCloseMember,
    });
  }
  if (scheduleEnabled) {
    fixedTabs.push({
      id: "schedule",
      // Display name only, and deliberately shorter than the route's own name
      // ("Practice Schedule" — PracticeSchedulePage, PracticeScheduleGate,
      // which all keep it): the tab bar now spells its labels out rather than
      // condensing to icons, and three full labels have to share the header
      // row with the wordmark. Same reasoning as "Patient Panel" below — the
      // id, route and feature-catalog key don't move for a display rename.
      label: "Schedule",
      to: "/schedule",
      active: active === "schedule",
      icon: <CalendarDays size={NAV_ICON_SIZE} aria-hidden="true" />,
    });
  }
  if (worklistEnabled) {
    fixedTabs.push({
      id: "worklist",
      // Display name only. The id, the route and the feature-catalog key stay
      // "worklist"/"PriorityWorklist" — they are wired into persona grants,
      // deep links and saved bookmarks, none of which should move for a rename.
      label: "Patient Panel",
      to: "/worklist",
      active: active === "worklist",
      icon: <Users size={NAV_ICON_SIZE} aria-hidden="true" />,
    });
    fixedTabs.push({
      id: "hospitalEd",
      // Same flag+persona gate as Patient Panel above (HospitalEdGate reuses
      // "PriorityWorklist") — a separate module id would be a new access
      // concept nobody has provisioned.
      label: "Hospital/ED",
      to: "/hospital-ed",
      active: active === "hospitalEd",
      icon: <Building2 size={NAV_ICON_SIZE} aria-hidden="true" />,
    });
  }

  const nav = <TabShellNav fixedTabs={fixedTabs} />;

  // Local const (not the `let` above) so the JSX below narrows it to non-null.
  const profile = effectiveProfile;

  return (
    <div className="h-full flex flex-col font-sans text-foreground bg-background overflow-hidden">
      {/* P6d — the extension side panel already carries its own branding
          (green bar/"AstraConnect"), so compose the tab bar into the SAME
          compact EmbeddedControls strip the classic (non-shell) embedded
          AppLayout uses, instead of the full standalone AppHeader (which
          would duplicate/clash with the extension's own chrome). Standalone
          web keeps the full AppHeader with logo/wordmark. */}
      {EMBEDDED ? (
        <EmbeddedControls
          accountLabel={accountLabel}
          onSignOut={() => void logout()}
          nav={nav}
        />
      ) : (
        <AppHeader accountLabel={accountLabel} onSignOut={() => void logout()} nav={nav} />
      )}
      <main className="flex-1 min-h-0 relative">
        {/* Home — always mounted, no lazy-mount latch: unlike Worklist/Schedule
            it fetches nothing, so there's no query to defer until the tab is
            actually selected (same reasoning as Search below). */}
        <div hidden={active !== "home"} className="h-full min-h-0">
          <HomePage />
        </div>

        {/* Search — always mounted; owns its own scroll for scroll preservation. */}
        <div hidden={active !== "search"} className="h-full min-h-0 overflow-y-auto">
          <MemberSearchGate>
            {/* isActiveTab gates Search's auto-nav effects: Search stays mounted
                while hidden, so only the visible tab may drive navigation. */}
            <MemberSearchPage isActiveTab={active === "search"} />
          </MemberSearchGate>
        </div>

        {/* Practice Schedule — mounted only when the persona grants it AND the
            tab has been activated at least once (lazy mount — see
            scheduleActivated above); once mounted, stays mounted (owns its own
            date/NPI/scroll state, preserved while kept alive). Wrapped in the
            same PracticeScheduleGate the /schedule route uses so the panel body
            fails closed on a hard persona/error edge even once mounted. */}
        {scheduleEnabled && scheduleActivated && (
          <div hidden={active !== "schedule"} className="h-full min-h-0 overflow-y-auto">
            <PracticeScheduleGate>
              <PracticeSchedulePage />
            </PracticeScheduleGate>
          </div>
        )}

        {/* Worklist — mounted only when flag+persona allow AND the tab has
            been activated at least once (lazy mount — see worklistActivated
            above); once mounted, stays mounted (module owns its own
            filters/sort/scroll state, preserved while kept alive). */}
        {worklistEnabled && worklistActivated && (
          <div hidden={active !== "worklist"} className="h-full min-h-0">
            <WorklistPage />
          </div>
        )}

        {/* Hospital/ED — same lazy-mount/keep-alive contract as Worklist
            above, gated on the SAME `worklistEnabled` flag+persona check. */}
        {worklistEnabled && hospitalEdActivated && (
          <div hidden={active !== "hospitalEd"} className="h-full min-h-0">
            <HospitalEdPage />
          </div>
        )}

        {/* The single open member, rendered INTO the Search tab's slot — no
            member tab of its own. Stays mounted (hidden) while the search
            results show, so returning to it is instant and preserves in-flight
            work; `key={navId}` gives a genuinely different member a clean
            instance, and unmounts the one it replaced. */}
        {profile && (
          <div hidden={active !== "profile"} className="h-full min-h-0">
            <MemberProfilePage
              key={profile.navId}
              hideGlobalChrome
              navIdOverride={profile.navId}
              locationStateOverride={
                (profile.location.state as MemberProfilePageProps["locationStateOverride"]) ??
                null
              }
              onMemberResolved={(name) => handleMemberResolved(profile.navId, name)}
              // Only a VISIBLE profile may run the EHR patient-switch auto-nav;
              // mounted-but-hidden must not fire navigate().
              isActiveTab={active === "profile"}
            />
          </div>
        )}
      </main>
    </div>
  );
}
